import React, { useState, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import useApiContext from 'hooks/useApiContext';
import useModalContext from 'hooks/useModalContext';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Alert,
  Box,
  Typography,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import ReorderIcon from '@mui/icons-material/Reorder';
import { useLocation, Outlet, useParams, useNavigate } from 'react-router-dom';
import { format, isAfter } from 'date-fns';
import useRepo from 'hooks/useRepo';
import { T, A } from 'TOMA';
import s from './ExhibitorList.module.scss';
import routes from 'router/routes';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Switch } from 'components/Switch';
import { CustomLink } from 'components/CustomLink';
import { eRoles } from '../../interfaces/Roles';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'react-i18next';

const columnHelper = createColumnHelper<any>();

const SIDEBAR_WIDTH = 230;
// Height of the app's sticky top header (#app-header Toolbar minHeight), so the
// fixed sidebar starts below it instead of being hidden underneath.
const HEADER_HEIGHT = 70;

interface SidebarItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  matchFn: (p: string) => boolean;
}

function Exhibitors(): JSX.Element {
  const [csvError, setCsvError] = useState<string | undefined>(undefined);
  const [csvSuccess, setCsvSuccess] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const { setModalContent, setModalVisible } = useModalContext();
  const { pathname } = useLocation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const {
    userRepository,
    exhibitorRepository,
    exhibitorEventRepository,
    codificationRepository,
    preferenceRepository,
  } = useApiContext();
  const user = userRepository.getUser();
  const isModerator = user?.role === eRoles.MODERATOR;
  const isAdmin = user?.role === eRoles.ADMIN;
  const [filterCollectif, setFilterCollectif] = useState<any>();
  const [filterName, setFilterName] = useState<any>();
  const [reload, setReload] = useState<number>(0);
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    response = [],
    loading,
    error,
  } = useRepo(async () => {
    if (routes.home.path.includes(pathname)) {
      return await exhibitorRepository.getExhibitorsWithoutEvent();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return await exhibitorRepository.getExhibitors(eventId!);
    }
  }, [reload]);

  const downloadCSV = async (): Promise<void> => {
    try {
      await exhibitorRepository.downloadCSV(eventId).then((e) => {
        setCsvData(e.data.data);
      });
    } catch (err) {
      console.error('Error downloading the file:', err);
    }
  };

  const { response: dateLimit } = useRepo(
    async () => await preferenceRepository.find('date_limit'),
    [reload]
  );

  const { response: codifCollectif } = useRepo(
    async () => await codificationRepository.get('collectif')
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('remove', {
        header: '',
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue();
          return (
            <button
              key={val.exhibId}
              type="button"
              className={s.ExhibitorList__remove}
              onClick={() => {
                setModalContent(
                  <div>
                    <A.Text fontSize="22" fw="bold" mb="20">
                      Supprimer l&apos;exposant &quot;{val.exhibName}&quot; ?
                    </A.Text>
                    <T.Flex gap={2} valign="center" mb="-15">
                      <A.Button
                        bg="alert"
                        onClick={() => {
                          userRepository.exhiRemove(val.exhibId).then(() => {
                            setModalVisible(false);
                            setReload((e) => e + 1);
                          });
                        }}
                      >
                        Confirmer
                      </A.Button>
                      <A.Button
                        appearance="link"
                        color="grey-80"
                        mb="15"
                        textDecoration="underline"
                        onClick={() => setModalVisible(false)}
                      >
                        Annuler
                      </A.Button>
                    </T.Flex>
                  </div>
                );
              }}
            >
              {val.removed ? (
                <A.Icon icon="trash3-fill" color="alert" fontSize="22" />
              ) : (
                <A.Icon icon="trash2" color="alert" fontSize="22" />
              )}
            </button>
          );
        },
      }),
      columnHelper.accessor('number', {
        header: 'ID',
      }),
      columnHelper.accessor('id_ITL', {
        header: 'ID structure',
      }),
      columnHelper.accessor('active', {
        header: t('event:active'),
        enableSorting: false,
        cell: ({ getValue }) => {
          const { value, userId, exhibId } = getValue();
          return (
            <Switch
              key={exhibId}
              defaultChecked={value}
              onChange={async (e) =>
                await userRepository.updateUserActive(e, userId)
              }
            />
          );
        },
      }),
      columnHelper.accessor('exhibName', {
        header: t('exhibitors:name_of_the_company'),
        cell: (info) => {
          const val = info.getValue();
          return (
            <CustomLink to={`${pathname}/exhibitor/${val.exhibId}`}>
              <b>{val.exhibName}</b>
            </CustomLink>
          );
        },
      }),
      columnHelper.accessor('lastLogin', {
        header: t('exhibitors:last_login'),
        cell: (info) => {
          const val = info.getValue();
          return val.value
            ? format(new Date(val.value), 'dd/MM/yyyy HH:mm')
            : '-';
        },
      }),
      columnHelper.accessor('products', {
        header: t('exhibitors:products_catalog'),
        cell: (info) => {
          const val = info.getValue();
          return (
            <CustomLink to={`${routes.home.path}/${val.exhibId}/product`}>
              <ReorderIcon />
            </CustomLink>
          );
        },
      }),
    ],
    []
  );

  const [csvData, setCsvData] = useState<any[]>([]);
  const [selectedAction, setSelectedAction] = useState<string>('');

  const handleActionChange = async (
    event: React.ChangeEvent<{ value: unknown }>
  ): Promise<void> => {
    const action = event.target.value as string;
    setSelectedAction(action);

    switch (action) {
      case 'addExhibitors':
        break;
      case 'exportSimple':
        break;
      case 'uploadCSV':
        break;
      case 'extractComplete':
        break;
      case 'exportExhibitors':
        break;
      default:
        break;
    }
  };

  const data = useMemo(
    () =>
      response
        .filter((e) => {
          if (filterCollectif === 'individuel') {
            return e.exhibitor_stand_type === filterCollectif;
          }
          if (filterCollectif) {
            return e.exhibitor_collectif_type === filterCollectif;
          }
          return true;
        })
        .filter((e) => {
          const regex = new RegExp(filterName, 'gi');
          return filterName
            ? regex.test(e.exhibitor_raison_sociale ?? '')
            : true;
        })
        .map((e) => {
          let collectif = '';
          if (e.exhibitor_stand_type === 'individuel') {
            collectif = 'Individuel';
          } else {
            collectif = e.exhibitor_collectif_type
              ? codifCollectif?.find(
                  (ee) => ee.topicCode === e.exhibitor_collectif_type
                )?.label ?? ''
              : collectif;
          }
          return {
            remove: {
              userId: e.user_id,
              exhibId: e.exhibitor_id,
              exhibName: e.exhibitor_raison_sociale,
              removed: e.removed,
            },
            new: {
              isNew: isAfter(new Date(e.user_created_at), new Date(2023, 8, 1)),
            },
            status: e.exhibitor_locked
              ? 'Transmis'
              : e.exhibitor_raison_sociale
              ? 'Enregistré'
              : e.user_email_verified_at
              ? 'En cours'
              : 'En attente',
            active: {
              value: !!e.user_email_verified_at,
              userId: e.user_id,
              exhibId: e.exhibitor_id,
            },
            visibility: {
              value: !!e.exhibitor_visibility,
              exhibId: e.exhibitor_id,
            },
            locked: {
              value: !!e.exhibitor_locked,
              exhibId: e.exhibitor_id,
            },
            id: e.exhibitor_id,
            userEmail: e.user_email,
            number: `ID ${String(e.exhibitor_id).padStart(4, '0')}`,
            id_ITL: e.exhibitor_id_ITL
              ? `ID ${String(e.exhibitor_id_ITL).padStart(4, '0')}`
              : '-',
            showroom: {
              value: !!e.exhibitor_showroom,
              exhibId: e.exhibitor_id,
              exhibEventId: e.exhibitor_event_id ?? 0,
            },
            raisonSocial: e.exhibitor_raison_sociale,
            exhibName: {
              exhibId: e.exhibitor_id,
              exhibName: e.exhibitor_raison_sociale,
            },
            nomCommercial: e.exhibitor_nom_societe,
            collectif,
            year: { year: e.year, exhibId: e.exhibitor_id },
            numeroHall: {
              stand: e.exhibitor_stand,
              hall: e.exhibitor_hall,
              exhibId: e.exhibitor_id,
            },
            edit: {
              exhibitorId: e.exhibitor_id,
            },
            products: {
              value: !!e.user_email_verified_at,
              userId: e.user_id,
              exhibId: e.exhibitor_id,
            },
            lastLogin: {
              value: e.user_last_login_at,
              userId: e.user_id,
              exhibId: e.exhibitor_id,
            },
          };
        }),
    [response, codifCollectif, filterCollectif, filterName]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    manualPagination: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const isDrawerOpen = pathname !== routes.home.path;

  const closeDrawer = (): void => {
    setReload((prev) => prev + 1);
    navigate(routes.home.path);
  };

  const sidebarItems = useMemo<SidebarItem[]>(() => {
    if (isAdmin) {
      return [
        {
          label: t('header:users'),
          icon: <PeopleIcon fontSize="small" />,
          path: routes.profile.path,
          matchFn: (p) => p.includes('users'),
        },
        {
          label: t('header:events'),
          icon: <CalendarMonthIcon fontSize="small" />,
          path: routes.admEventList.path,
          matchFn: (p) => p.includes('events'),
        },
        {
          label: t('header:exhibitor'),
          icon: <GroupsIcon fontSize="small" />,
          path: routes.home.path,
          matchFn: (p) => p.includes('exhibitor'),
        },
        {
          label: t('header:company_settings'),
          icon: <BusinessIcon fontSize="small" />,
          path: routes.company.path,
          matchFn: (p) => p.includes('company'),
        },
      ];
    }
    if (isModerator) {
      return [
        {
          label: t('header:events'),
          icon: <CalendarMonthIcon fontSize="small" />,
          path: routes.admEventList.path,
          matchFn: (p) => p.includes('events'),
        },
        {
          label: t('header:exhibitor'),
          icon: <GroupsIcon fontSize="small" />,
          path: routes.home.path,
          matchFn: (p) => p.includes('exhibitor'),
        },
      ];
    }
    return [];
  }, [isAdmin, isModerator, t]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      {/* ── Left sidebar ── */}
      {sidebarItems.length > 0 && (
        <Box
          component="nav"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: '#fff',
            borderRight: '1px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: HEADER_HEIGHT,
            left: 0,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflowY: 'hidden',
          }}
        >
          {/* Sidebar header */}
          <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  bgcolor: '#f0fdf4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <GroupsIcon sx={{ fontSize: 18, color: '#16a34a' }} />
              </Box>
              <Typography
                sx={{ fontSize: '13px', fontWeight: 600, color: '#111' }}
              >
                {t('header:exhibitor')}
              </Typography>
            </Box>
          </Box>

          {/* Nav items */}
          <List disablePadding sx={{ py: 1.5, flex: 1 }}>
            {sidebarItems.map((item) => {
              const isActive = item.matchFn(pathname);
              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    component={CustomLink as any}
                    to={item.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      px: 2,
                      py: 0.875,
                      mx: 1,
                      mb: 0.25,
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#f5f5f5' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive ? '#eeeeee' : '#f9f9f9',
                      },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 30, color: isActive ? '#111' : '#bbb' }}
                    >
                      {isActive ? (
                        <CheckIcon sx={{ fontSize: 17 }} />
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#111' : '#666',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {/* ── Main content ── */}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, md: 4 },
          py: 3,
          minWidth: 0,
          marginLeft: sidebarItems.length > 0 ? `${SIDEBAR_WIDTH}px` : 0,
        }}
      >
        {/* Page header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}
            >
              {t('exhibitors:list_of_exhibitors')}
            </Typography>
            {!loading && (
              <Typography sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
                {data.length} exposant{data.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Search */}
            <TextField
              size="small"
              placeholder={t('event:find_an_exhibitors')}
              onChange={(e) => setFilterName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px', fontSize: 13, bgcolor: '#fff' },
              }}
              sx={{ width: 240 }}
            />

            {/* Import CSV */}
            <Button
              variant="outlined"
              size="small"
              sx={{
                height: 40,
                fontSize: 13,
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#ddd',
                color: '#333',
                bgcolor: '#fff',
              }}
              onClick={() => {
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = '.csv, .xls, .xlsx';
                fileInput.onchange = async ({ target }: any) => {
                  setCsvLoading(true);
                  setCsvSuccess(false);
                  setCsvError('');
                  if (!target.files) return;
                  const file = target.files[0];
                  const formdata = new FormData();
                  formdata.append('file', file);
                  try {
                    await exhibitorRepository.uploadExhiCSV(formdata);
                    setCsvSuccess(true);
                    setReload((e) => e + 1);
                  } catch (err: any) {
                    setCsvError(`${err.message}`);
                  } finally {
                    setCsvLoading(false);
                  }
                };
                fileInput.click();
              }}
            >
              {t('exhibitors:import_exhibitor_database')}
            </Button>

            {/* Actions dropdown */}
            <FormControl
              variant="outlined"
              size="small"
              sx={{ width: 160, bgcolor: '#fff' }}
            >
              <InputLabel id="actions-select-label" sx={{ fontSize: 13 }}>
                Actions
              </InputLabel>
              <Select
                labelId="actions-select-label"
                value={selectedAction}
                // @ts-expect-error
                onChange={handleActionChange}
                label={t('exhibitors:action')}
                sx={{ borderRadius: '8px', fontSize: 13 }}
              >
                <MenuItem value="exportSimple">
                  <CSVLink
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    filename={`export${
                      filterCollectif ? `-${filterCollectif}` : ''
                    }-${format(new Date(), 'dd-MM-yyyy-HH-mm-ss')}.csv`}
                    separator={';'}
                    enclosingCharacter={'"'}
                    data={[
                      ['ID', 'ID structure', 'Raison social'],
                      ...data.map((e) => [
                        e.id,
                        e.id_ITL,
                        e.raisonSocial?.replace(/"/g, '""'),
                      ]),
                    ]}
                  >
                    {t('exhibitors:simple_extraction')}
                  </CSVLink>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Loading */}
        {(loading || csvLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: '#000' }} />
          </Box>
        )}

        {/* Table */}
        {!loading && !csvLoading && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              border: '1px solid #eee',
              overflowX: 'auto',
            }}
          >
            <table className={s.Table} style={{ minWidth: 700 }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getIsSorted() === 'asc'
                          ? ' ↑'
                          : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className={s.ExhibitorList__row}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        <div className={s.ExhibitorList__row__div}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      {/* ── Right-side Drawer for exhibitor edit ── */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: pathname.includes('product')
              ? { xs: '100vw', sm: 900 }
              : { xs: '100vw', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {!pathname.includes('product') && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: '1px solid #eee',
              position: 'sticky',
              top: 0,
              bgcolor: '#fff',
              zIndex: 1,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
              {t('exhibitors:edit')}
            </Typography>
            <IconButton size="small" onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Outlet />
        </Box>
      </Drawer>

      {/* Error snackbar */}
      {csvError !== '' && csvError !== undefined && (
        <Snackbar
          open={csvError !== ''}
          autoHideDuration={6000}
          onClose={() => setCsvError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCsvError('')}
            severity="error"
            sx={{ width: '100%' }}
          >
            {csvError}
          </Alert>
        </Snackbar>
      )}

      {/* Success snackbar */}
      {csvSuccess && (
        <Snackbar
          open={csvSuccess}
          autoHideDuration={6000}
          onClose={() => setCsvSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCsvSuccess(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            {'File imported successfully'}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
export default Exhibitors;
